import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FIRST_TASK_EVENT } from './firstTaskRegistry';
import { onboardingApi } from './onboarding.service';
import { ONBOARDING_ENABLED, SETUP_V2_ENABLED, IMPORT_MAX_FILE_BYTES, IMPORT_MAX_FILES_PER_JOB } from './flags';
import { prereadFile, kindFromName } from './localPreread';

/**
 * Router-level state for the AI onboarding / import feature (plan §5).
 *
 * Lives above the routes (next to NewBookingHost) so:
 *  - a running ImportJob outlives the layer being closed, and the diary
 *    checklist / header can read its progress;
 *  - the diary FAB, the Clients → Import page and the first-login redirect
 *    all open the same layer.
 *
 * Class components (DiaryV2) talk to it through a window event:
 *   window.dispatchEvent(new CustomEvent('beu:onboarding-open', { detail: { mode, screen } }))
 * Function components use `useOnboarding()`.
 */

const OnboardingContext = createContext(null);

const POLL_MS = 2000;
const MIN_READING_MS = 750; // a row never flickers straight to "recognised"
const FACTS_MIN_MS = 10 * 1000; // facts are re-read at most this often, unless setup opens

const isLoggedIn = () => !!localStorage.getItem('accessToken');

/**
 * Whose onboarding state this is: the signed-in person at the site this
 * device is on. Logging out and in again (or as someone else) happens
 * without a page reload, so the state is reloaded whenever this changes,
 * and nothing read for one person is ever shown to, or merged into, the next.
 */
export function onboardingIdentity(user) {
  const info = (user && user.userInfo) || {};
  let userId = info.userId || info._id || '';
  let salonId = '';
  try {
    userId = userId || localStorage.getItem('userId') || '';
    salonId = localStorage.getItem('salonId') || '';
  } catch (e) { /* storage blocked */ }
  const h = info.hairdresser || {};
  if (!salonId && h.salon) salonId = typeof h.salon === 'object' ? String(h.salon._id || '') : String(h.salon);
  return { userId: String(userId || ''), salonId: String(salonId || ''), key: isLoggedIn() && userId ? `${userId}:${salonId}` : '' };
}

/**
 * The checklist flags as the server keeps them. Older rows hold booleans
 * where the setup plan now writes a word: live === true is 'done', and
 * import === true is 'started'.
 */
export const isLiveDone = (checklist) => !!checklist && (checklist.live === 'done' || checklist.live === true);
export const isImportStarted = (checklist) => !!checklist && (checklist.import === 'started' || checklist.import === true);

export function OnboardingProvider({ children }) {
  const user = useSelector((st) => st.user);
  const identity = onboardingIdentity(user).key;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  // Layer
  const [layer, setLayer] = useState({ open: false, mode: 'setup', screen: null });
  // Server-side onboarding state (status/checklist). null until loaded.
  const [onboarding, setOnboarding] = useState(null);
  // True once the server's copy has really loaded (not the fail-open stand-in),
  // so nothing overwrites server progress with a stale browser copy.
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  // Whose state `onboarding` is (see onboardingIdentity), so a reader can tell
  // the new person's state from the last person's in the render between.
  const [onboardingFor, setOnboardingFor] = useState('');
  const loadTries = useRef(0);
  // Active job as last polled.
  const [job, setJob] = useState(null);
  // Locally staged files (before the server knows them / while uploading).
  const [localFiles, setLocalFiles] = useState([]);
  const [error, setError] = useState('');
  const jobIdRef = useRef(null);
  const pollRef = useRef(null);
  const stagedAtRef = useRef({});
  const modeRef = useRef('setup'); // 'setup' (first-run, tracked in onboarding state) | 'import' (post-setup, independent job)

  /* ---------- onboarding state ---------- */
  const loadOnboarding = useCallback(async () => {
    if (!ONBOARDING_ENABLED || !isLoggedIn()) return null;
    const who = identityRef.current;
    try {
      const s = await onboardingApi.getOnboardingState();
      // Signed out, or in as someone else, while it was on its way: not theirs.
      if (identityRef.current !== who) return null;
      setOnboarding(s);
      setOnboardingLoaded(true);
      setOnboardingFor(who);
      loadTries.current = 0;
      if (s?.activeJobId && !jobIdRef.current) jobIdRef.current = s.activeJobId;
      return s;
    } catch (e) {
      if (identityRef.current !== who) return null;
      // Fail open: no state means "new", the layer will still offer itself
      // once. Try again in a moment (a few times), so a blip on sign-in
      // doesn't leave the server copy unread for the whole visit.
      setOnboarding((prev) => prev || { status: 'new', checklist: {} });
      loadTries.current += 1;
      if (loadTries.current <= 3) {
        setTimeout(() => { if (identityRef.current === who) loadOnboarding(); }, 4000 * loadTries.current); // eslint-disable-line no-use-before-define
      }
      return null;
    }
  }, []);

  // Resolves true once the server has it, false if it didn't take (callers
  // that must not lose a write can try again).
  const patchOnboarding = useCallback(async (patch) => {
    setOnboarding((prev) => ({ ...(prev || { status: 'new', checklist: {} }), ...patch, checklist: { ...((prev || {}).checklist || {}), ...(patch.checklist || {}) } }));
    const who = identityRef.current;
    try {
      const s = await onboardingApi.putOnboardingState(patch);
      // The reply carries the salon's facts as they are now: keep those
      // (the rest of it is what we just sent).
      if (s && s.facts && identityRef.current === who) setOnboarding((prev) => (prev ? { ...prev, facts: s.facts } : prev));
      return true;
    } catch (e) { return false; }
  }, []);

  // Setup ticks a chapter when the salon already has the thing (services in
  // the catalogue, hours in Team, payments connected). That's read on the
  // server (onboarding.facts), and was only read at sign-in, so work done
  // elsewhere never ticked until a full reload. Read the facts again on
  // each page change, when the tab comes back, and when setup opens: only
  // the facts are taken, so nothing races the plan's own writes.
  const onboardingRef = useRef(onboarding);
  onboardingRef.current = onboarding;
  const factsAt = useRef(0);
  const refreshFacts = useCallback(async ({ force = false } = {}) => {
    const cur = onboardingRef.current;
    if (!ONBOARDING_ENABLED || !isLoggedIn() || !cur || cur.status === 'completed') return;
    if (!force && Date.now() - factsAt.current < FACTS_MIN_MS) return;
    factsAt.current = Date.now();
    const who = identityRef.current;
    try {
      const s = await onboardingApi.getOnboardingState();
      if (s && s.facts && identityRef.current === who) setOnboarding((prev) => (prev ? { ...prev, facts: s.facts } : prev));
    } catch (e) { /* the facts we have stand */ }
  }, []);
  const { pathname } = useLocation();
  useEffect(() => { refreshFacts(); }, [pathname, refreshFacts]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') refreshFacts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshFacts]);

  // Guided first tasks: any page can report "the owner just did X". This lives
  // here, not in the diary card, so a task done on Clients or the catalogue
  // still counts. Ignored until setup has been opened at least once.
  useEffect(() => {
    if (!ONBOARDING_ENABLED) return undefined;
    const on = (e) => {
      const key = e.detail && e.detail.key;
      const cur = onboardingRef.current;
      if (!key || !cur || cur.status === 'new') return;
      const tasks = (cur.checklist && cur.checklist.tasks) || {};
      // A paid practice booking must never be cancelled at go-live.
      const practice = cur.checklist && cur.checklist.practice;
      if (key === 'pay' && practice && practice.bookingId && e.detail.bookingId === practice.bookingId && !practice.paid) {
        patchOnboarding({ checklist: { practice: { ...practice, paid: true } } });
      }
      // Set up with Bailey ticks its practice tasks itself (SetupContext),
      // and only while the owner is on the practice run, so an everyday
      // booking made weeks later never counts as practice.
      if (SETUP_V2_ENABLED) return;
      if (tasks[key] === 'done') return;
      // The checklist merge is shallow: always send the whole tasks map.
      patchOnboarding({ checklist: { tasks: { ...tasks, [key]: 'done' } } });
    };
    window.addEventListener(FIRST_TASK_EVENT, on);
    return () => window.removeEventListener(FIRST_TASK_EVENT, on);
  }, [patchOnboarding]);

  /* ---------- polling ---------- */
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const refresh = useCallback(async () => {
    const id = jobIdRef.current;
    if (!id) return null;
    try {
      const j = await onboardingApi.getJob(id);
      // Honour the minimum "reading" time per file so the UI never flickers.
      const now = Date.now();
      j.files = (j.files || []).map((f) => {
        const t = stagedAtRef.current[f.fileId];
        if (f.status !== 'reading' && t && now - t < MIN_READING_MS) return { ...f, status: 'reading', _pendingStatus: f.status };
        return f;
      });
      setJob(j);
      if (!['extracting', 'committing'].includes(j.status) && !j.files.some((f) => f.status === 'reading')) stopPolling();
      return j;
    } catch (e) {
      setError(e.message || 'Could not read the import job.');
      stopPolling();
      return null;
    }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    refresh();
    pollRef.current = setInterval(refresh, POLL_MS);
  }, [refresh]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Load on arrival, and again for whoever signs in next: a sign-out and
  // sign-in happens without a page reload, and the last person's state, job
  // and files must never carry over to them.
  const loadedFor = useRef(null);
  useEffect(() => {
    if (loadedFor.current === identity) return;
    const first = loadedFor.current === null;
    loadedFor.current = identity;
    if (!first) {
      stopPolling();
      jobIdRef.current = null;
      stagedAtRef.current = {};
      loadTries.current = 0;
      setJob(null);
      setLocalFiles([]);
      setError('');
      setOnboarding(null);
      setOnboardingLoaded(false);
      setOnboardingFor('');
      setLayer((l) => ({ ...l, open: false }));
    }
    if (identity) loadOnboarding();
  }, [identity, loadOnboarding, stopPolling]);

  // Resume a job we already know about (state from server, or page reload).
  useEffect(() => {
    if (jobIdRef.current && !job) startPolling();
  }, [onboarding, job, startPolling]);

  /* ---------- layer ---------- */
  const openLayer = useCallback((opts = {}) => {
    const mode = opts.mode || 'setup';
    modeRef.current = mode;
    setLayer({ open: true, mode, screen: opts.screen || null });
    if (mode === 'setup') {
      if (onboarding?.status === 'new') patchOnboarding({ status: 'in_progress' });
      // Re-attach to the setup job if a post-setup import swapped it out.
      const setupJobId = onboarding?.activeJobId;
      if (setupJobId && jobIdRef.current !== setupJobId) {
        stopPolling();
        jobIdRef.current = setupJobId;
        setJob(null);
        setLocalFiles([]);
        startPolling();
      }
    }
  }, [onboarding, patchOnboarding, stopPolling, startPolling]);

  const closeLayer = useCallback(() => setLayer((l) => ({ ...l, open: false })), []);

  useEffect(() => {
    const onOpen = (e) => openLayer(e.detail || {});
    window.addEventListener('beu:onboarding-open', onOpen);
    return () => window.removeEventListener('beu:onboarding-open', onOpen);
  }, [openLayer]);

  /* ---------- files ---------- */
  // opts.mode: 'setup' always records the job as the setup's own, whatever
  // was opened earlier in the session (Set up with Bailey passes it).
  const addFiles = useCallback(async (fileList, opts = {}) => {
    setError('');
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const existingCount = localFiles.length;
    const room = Math.max(IMPORT_MAX_FILES_PER_JOB - existingCount, 0);
    const tooMany = incoming.length > room;
    const accepted = incoming.slice(0, room);
    const rejectedSize = accepted.filter((f) => f.size > IMPORT_MAX_FILE_BYTES);
    const ok = accepted.filter((f) => f.size <= IMPORT_MAX_FILE_BYTES);
    if (tooMany) setError(`You can add up to ${IMPORT_MAX_FILES_PER_JOB} files per import. The first ${room} were added.`);
    else if (rejectedSize.length) setError(`${rejectedSize.map((f) => f.name).join(', ')} ${rejectedSize.length > 1 ? 'are' : 'is'} over 25 MB and was skipped.`);
    if (!ok.length) return;

    const staged = ok.map((file, i) => {
      const { kind, isDoc } = kindFromName(file);
      return { localId: `${Date.now()}-${i}-${file.name}`, file, name: file.name, kind, isDoc, status: 'uploading', preread: null, fileId: null };
    });
    setLocalFiles((prev) => [...prev, ...staged]);

    // Pre-read in parallel with the upload for the instant tag.
    staged.forEach((s) => {
      prereadFile(s.file).then((pre) => {
        setLocalFiles((prev) => prev.map((x) => (x.localId === s.localId ? { ...x, preread: pre } : x)));
      });
    });

    const uploaded = await Promise.all(staged.map(async (s) => {
      try {
        const meta = await onboardingApi.uploadFile(s.file);
        stagedAtRef.current[meta.fileId] = Date.now();
        setLocalFiles((prev) => prev.map((x) => (x.localId === s.localId ? { ...x, fileId: meta.fileId, status: 'reading' } : x)));
        return meta.fileId;
      } catch (e) {
        // Keep the server's reason: it says "Photos and PDFs are not supported
        // yet" or "over 25 MB"; a bare "Could not upload" hid all of that.
        const failReason = (e && e.message && e.message !== 'Request failed') ? e.message : '';
        setLocalFiles((prev) => prev.map((x) => (x.localId === s.localId ? { ...x, status: 'failed', failReason } : x)));
        return null;
      }
    }));
    const fileIds = uploaded.filter(Boolean);
    if (!fileIds.length) return;

    try {
      if (!jobIdRef.current) {
        const created = await onboardingApi.createJob(fileIds);
        jobIdRef.current = created.jobId;
        // Only the first-run setup is tracked in onboarding state; a later
        // "Bring more in" is its own job and must not reopen the checklist.
        if ((opts.mode || modeRef.current) === 'setup') patchOnboarding({ activeJobId: created.jobId, status: 'in_progress', checklist: { import: 'started' } });
      } else {
        await onboardingApi.addFilesToJob(jobIdRef.current, fileIds);
      }
      startPolling();
    } catch (e) {
      setError(e.message || 'Could not start reading your files.');
    }
  }, [localFiles.length, patchOnboarding, startPolling]);

  const removeLocalFile = useCallback((localId) => {
    setLocalFiles((prev) => prev.filter((f) => f.localId !== localId));
  }, []);

  /* ---------- job actions ---------- */
  const withJob = (fn) => async (...args) => {
    const id = jobIdRef.current;
    if (!id) throw new Error('No active import');
    return fn(id, ...args);
  };

  const answer = useCallback(withJob(async (id, itemId, ans) => {
    await onboardingApi.answer(id, itemId, ans);
    setJob((j) => (j ? { ...j, answers: { ...(j.answers || {}), [itemId]: ans }, summary: { ...(j.summary || {}), questions: Math.max(((j.summary || {}).questions || 1) - 1, 0) } } : j));
  }), []);

  const mergeExact = useCallback(withJob(async (id) => {
    const r = await onboardingApi.mergeExact(id);
    setJob((j) => (j ? { ...j, exactMerged: true } : j));
    return r;
  }), []);

  const commit = useCallback(withJob(async (id) => {
    await onboardingApi.commit(id);
    if (modeRef.current === 'setup') patchOnboarding({ checklist: { import: 'done' } });
    startPolling();
  }), [patchOnboarding, startPolling]);

  const rollback = useCallback(async (jobId) => {
    const r = await onboardingApi.rollback(jobId || jobIdRef.current);
    if (!jobId || jobId === jobIdRef.current) {
      jobIdRef.current = null;
      setJob(null);
      setLocalFiles([]);
      // false, not a word: the server keeps only 'started' as a word and
      // would read anything else as true (started).
      if (modeRef.current === 'setup') patchOnboarding({ activeJobId: null, checklist: { import: false } });
    }
    return r;
  }, [patchOnboarding]);

  // A fresh import (post-setup "Bring more in") starts its own job.
  const startNewJob = useCallback(() => {
    stopPolling();
    jobIdRef.current = null;
    setJob(null);
    setLocalFiles([]);
    setError('');
  }, [stopPolling]);

  /* ---------- derived: the staging rows the UI renders ---------- */
  const stagedRows = useMemo(() => {
    const byFileId = {};
    (job?.files || []).forEach((f) => { byFileId[f.fileId] = f; });
    const seen = new Set();
    const rows = localFiles.map((lf) => {
      const srv = lf.fileId ? byFileId[lf.fileId] : null;
      if (lf.fileId) seen.add(lf.fileId);
      const status = lf.status === 'failed' ? 'failed' : srv ? srv.status : lf.status; // uploading | reading | recognised | needs_look | failed
      return { ...lf, status, analysis: srv?.analysis || null, failReason: lf.failReason || srv?.reason || srv?.error || '' };
    });
    // Files the server knows but we don't hold locally (page reload, or the
    // job was started on another device) still get a row.
    (job?.files || []).forEach((f) => {
      if (seen.has(f.fileId)) return;
      const { kind, isDoc } = kindFromName({ name: f.name || '', type: f.mime || '' });
      rows.push({ localId: `srv-${f.fileId}`, name: f.name || 'File', kind, isDoc, status: f.status, preread: null, fileId: f.fileId, analysis: f.analysis || null, failReason: f.reason || f.error || '' });
    });
    return rows;
  }, [localFiles, job]);

  const value = useMemo(() => ({
    enabled: ONBOARDING_ENABLED,
    layer, openLayer, closeLayer,
    onboarding, onboardingLoaded, patchOnboarding, loadOnboarding, refreshFacts,
    // Whose state is loaded, and whose it should be: equal once it's theirs.
    identity, onboardingFor,
    job, stagedRows, error, setError,
    addFiles, removeLocalFile, refresh,
    answer, mergeExact, commit, rollback, startNewJob,
  }), [layer, openLayer, closeLayer, onboarding, onboardingLoaded, identity, onboardingFor, patchOnboarding, loadOnboarding, refreshFacts, job, stagedRows, error, addFiles, removeLocalFile, refresh, answer, mergeExact, commit, rollback, startNewJob]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  return useContext(OnboardingContext);
}

export function openOnboardingLayer(detail = {}) {
  window.dispatchEvent(new CustomEvent('beu:onboarding-open', { detail }));
}

export default OnboardingContext;
